<template>
  <div class="cc-root">

    <!-- Full-bleed background -->
    <div class="cc-bg">
      <!-- <img class="cc-bg__img" src="@/assets/CruzControl_App_Login_Image_High_Resolution.png" alt="" /> -->
      <div class="cc-bg__overlay"></div>
    </div>

    <!-- Centered page -->
    <div class="cc-page">
      <article class="cc-card">

        <!-- Gradient accent bar -->
        <div class="cc-card__bar" aria-hidden="true"></div>

        <div class="cc-card__inner">

          <!-- Logo -->
          <img class="cc-logo cc-logo--dark" src="@/assets/CruzControl_Logo_Small_Icon.svg" alt="CruzControl" />
          <img class="cc-logo cc-logo--light" src="@/assets/CruzControl_Logo_Small_Icon_Light.svg" alt="CruzControl" />

          <!-- Headline -->
          <header class="cc-header">
            <h1 class="cc-header__title">SIGN IN</h1>
            <p class="cc-header__sub">Access your CruzControl dashboard</p>
          </header>

          <!-- Loading state -->
          <div v-if="submitting" class="cc-loading">
            <LoadingSpinner />
          </div>

          <!-- Form state -->
          <div v-if="!submitting">

            <!-- Error alert -->
            <div v-if="showError" class="cc-error" role="alert">
              <svg class="cc-error__icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <p class="cc-error__text animate-shake whitespace-pre">{{ errorMsg }}</p>
            </div>

            <form @submit.prevent="login" class="cc-form" novalidate>

              <!-- Email -->
              <div class="cc-field">
                <label class="cc-label" for="email">Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autocomplete="email"
                  required
                  v-model="email"
                  class="cc-input"
                  placeholder="name@company.com"
                />
              </div>

              <!-- Password -->
              <div class="cc-field">
                <label class="cc-label" for="password">Password</label>
                <div class="cc-pw-row">
                  <input
                    id="password"
                    name="password"
                    :type="show ? 'password' : 'text'"
                    autocomplete="current-password"
                    required
                    v-model="password"
                    class="cc-input cc-input--pw"
                    placeholder="••••••••"
                  />
                  <button type="button" @click="show = !show" class="cc-eye" aria-label="Toggle password visibility">
                    <font-awesome-icon icon="fa-solid fa-eye" />
                  </button>
                </div>
              </div>

              <!-- Forgot password -->
              <div class="cc-meta">
                <a href="/reset" class="cc-forgot">Forgot your password?</a>
              </div>

              <!-- Submit -->
              <button type="submit" class="cc-submit">
                <span class="cc-submit__label">Sign In</span>
                <span class="cc-submit__arrow" aria-hidden="true">→</span>
              </button>

            </form>
          </div>
        </div>

        <!-- Card footer -->
        <div class="cc-card__foot">
          <span class="cc-card__wordmark">CRUZCONTROL</span>
        </div>

      </article>
    </div>

  </div>
</template>

<script>

import { signIn, signOut, fetchAuthSession } from 'aws-amplify/auth';
import LoadingSpinner from "@/components/LoadingSpinner";
import {mapState, mapMutations} from "vuex";
import {store} from "@/store/store";
import {getUser} from "@/services/user.service";

export default {
  name: "loginPage",
  title: "Cruz Control | Login",
  components: {
    LoadingSpinner
  },
  data() {
    return {
      submitting: false,
      showError: false,
      errorMsg: "",
      noConnection: false,
      email: "",
      password: "",
      show: true,
    }
  },
  methods: {
    ...mapState(["user"]),
    ...mapMutations(["setUser"]),
    async waitForAuthSession({ maxAttempts = 10, delayMs = 200 } = {}) {
      let lastError = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const session = await fetchAuthSession();
          if (session?.tokens?.accessToken) {
            return session;
          }
        } catch (error) {
          lastError = error;
        }

        await new Promise(resolve => setTimeout(resolve, delayMs));
      }

      throw lastError || new Error("Unable to establish authenticated session.");
    },
    async login() {
      this.submitting = true
      this.showError = false
      this.errorMsg = ""

      try {
        // Clear any stale session so signIn doesn't throw "already signed in"
        try { await signOut(); } catch (_) { /* ignore if no session */ }

        const response = await signIn({ username: this.email, password: this.password })

        if (response.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          await store.commit("setEmail", this.email)
          await this.$router.push('/first_login')
          return
        }

        await this.waitForAuthSession()

        console.log('DEBUG email value:', this.email)
        const userResponse = await getUser(this.email)
        console.log('DEBUG userResponse:', userResponse)

        if (userResponse.length > 0) {
          const userData = userResponse[0]
          console.log('DEBUG userdata:', userData)
          if (userData.active) {
            await store.commit('setUser', userData)
            await this.$router.push('/')
          }
          else {
            this.errorMsg = `ACCOUNT INACTIVE. \nCONTACT SUPPORT (800)-555-5555`
            this.showError = true
          }
        }
        else {
          await signOut()
          this.errorMsg = `ACCOUNT NOT FOUND`
          this.showError = true
        }
      } catch (error) {
        console.log('Login error:', error)
        const msg = error?.message || error.toString()

        if (msg.includes("NotAuthorizedException")) {
          this.errorMsg = "INCORRECT EMAIL OR PASSWORD"
        } else if (msg.includes("UserNotFoundException")) {
          this.errorMsg = "ACCOUNT NOT FOUND"
        } else if (msg.includes("UserNotConfirmedException")) {
          this.errorMsg = "ACCOUNT NOT CONFIRMED.\nPLEASE CHECK YOUR EMAIL FOR A VERIFICATION LINK."
        } else if (msg.includes("PasswordResetRequiredException")) {
          this.errorMsg = "PASSWORD RESET REQUIRED.\nPLEASE USE THE FORGOT PASSWORD LINK BELOW."
        } else if (msg.includes("TooManyRequestsException") || msg.includes("LimitExceededException")) {
          this.errorMsg = "TOO MANY ATTEMPTS. PLEASE TRY AGAIN LATER."
        } else if (msg.includes("InvalidParameterException")) {
          this.errorMsg = "INVALID EMAIL OR PASSWORD FORMAT"
        } else if (msg.includes("InternalErrorException")) {
          this.errorMsg = "AN INTERNAL ERROR OCCURRED. PLEASE TRY AGAIN."
        } else if (msg.includes("NetworkError") || msg.includes("Failed to fetch") || msg.includes("Network request failed")) {
          this.errorMsg = "UNABLE TO CONNECT. PLEASE CHECK YOUR INTERNET CONNECTION."
        } else {
          this.errorMsg = msg
        }
        this.showError = true
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@700;800;900&family=Barlow:wght@300;400;500;600&display=swap');

/* ── Root ──────────────────────────────────────────── */
.cc-root {
  position: fixed;
  inset: 0;
  overflow: hidden;
  font-family: 'Barlow', sans-serif;
}

/* ── Background ────────────────────────────────────── */
.cc-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-color: var(--cc-page);
  transition: background-color 0.3s ease;
}

.cc-bg__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.cc-bg__overlay {
  position: absolute;
  inset: 0;
  background: var(--cc-overlay-grad);
  transition: background 0.3s ease;
}

/* ── Page ──────────────────────────────────────────── */
.cc-page {
  position: relative;
  z-index: 10;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

/* ── Card ──────────────────────────────────────────── */
.cc-card {
  width: 100%;
  max-width: 430px;
  background: var(--cc-card);
  backdrop-filter: blur(28px) saturate(150%);
  -webkit-backdrop-filter: blur(28px) saturate(150%);
  border: 1px solid var(--cc-card-border);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: var(--cc-card-shadow);
  animation: cardIn 0.65s cubic-bezier(0.16, 1, 0.3, 1) both;
  transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(22px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Top gradient bar */
.cc-card__bar {
  height: 3px;
  background: linear-gradient(90deg, #0092bf 0%, #00bcf5 55%, #fcef60 100%);
}

.cc-card__inner {
  padding: 2.25rem 2.5rem 0;
}

/* ── Logo ──────────────────────────────────────────── */
.cc-logo {
  height: 42px;
  width: auto;
  display: block;
  margin-bottom: 1.75rem;
}
.cc-logo--light { display: none; }
[data-theme="light"] .cc-logo--dark  { display: none; }
[data-theme="light"] .cc-logo--light { display: block; }

/* ── Header ────────────────────────────────────────── */
.cc-header {
  margin-bottom: 1.75rem;
}

.cc-header__title {
  font-family: 'Big Shoulders Display', sans-serif;
  font-size: 3.375rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  color: var(--cc-t1);
  line-height: 1;
  margin: 0 0 0.4rem;
  transition: color 0.3s;
}

.cc-header__sub {
  font-size: 0.8125rem;
  font-weight: 300;
  color: var(--cc-t3);
  letter-spacing: 0.02em;
  margin: 0;
  transition: color 0.3s;
}

/* ── Loading ───────────────────────────────────────── */
.cc-loading {
  padding: 2rem 0 1rem;
  display: flex;
  justify-content: center;
}

/* ── Error ─────────────────────────────────────────── */
.cc-error {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  background: rgba(239, 68, 68, 0.10);
  border-left: 3px solid #EF4444;
  padding: 0.75rem 1rem;
  border-radius: 0 2px 2px 0;
  margin-bottom: 1.25rem;
}

.cc-error__icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: #f87171;
  margin-top: 2px;
}

.cc-error__text {
  font-size: 0.8125rem;
  color: #fca5a5;
  font-weight: 500;
  margin: 0;
  line-height: 1.55;
}

/* ── Form ──────────────────────────────────────────── */
.cc-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.cc-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.cc-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--cc-t4);
  transition: color 0.3s;
}

/* ── Inputs ────────────────────────────────────────── */
.cc-input {
  width: 100%;
  box-sizing: border-box;
  background: var(--cc-input);
  border: 1px solid var(--cc-input-border);
  border-radius: 3px;
  padding: 0.75rem 1rem;
  font-family: 'Barlow', sans-serif;
  font-size: 0.9375rem;
  font-weight: 400;
  color: var(--cc-input-text);
  outline: none;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease,
    color 0.2s ease;
}

.cc-input::placeholder {
  color: var(--cc-placeholder);
}

.cc-input:focus {
  border-color: #0092bf;
  background: rgba(0, 146, 191, 0.07);
  box-shadow: 0 0 0 3px rgba(0, 146, 191, 0.14);
}

/* Override browser autofill background */
.cc-input:-webkit-autofill,
.cc-input:-webkit-autofill:hover,
.cc-input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px var(--cc-autofill-bg) inset;
  -webkit-text-fill-color: var(--cc-input-text);
  border-color: var(--cc-input-border);
  caret-color: var(--cc-input-text);
}

/* Password row */
.cc-pw-row {
  display: flex;
}

.cc-input--pw {
  border-radius: 3px 0 0 3px;
  flex: 1;
  min-width: 0;
}

.cc-eye {
  flex-shrink: 0;
  background: var(--cc-eye-bg);
  border: 1px solid var(--cc-eye-border);
  border-left: none;
  border-radius: 0 3px 3px 0;
  padding: 0 1rem;
  color: var(--cc-eye-icon);
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  transition: background 0.18s, color 0.18s;
}

.cc-eye:hover {
  background: var(--cc-toggle-hover);
  color: var(--cc-t1);
}

.cc-eye:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 146, 191, 0.14);
}

/* ── Forgot password ───────────────────────────────── */
.cc-meta {
  display: flex;
  justify-content: flex-end;
  margin-top: -0.375rem;
}

.cc-forgot {
  font-size: 0.75rem;
  font-weight: 500;
  color: #8f861f;
  text-decoration: none;
  letter-spacing: 0.01em;
  opacity: 0.8;
  transition: opacity 0.18s;
}

.cc-forgot:hover {
  opacity: 1;
  text-decoration: underline;
}
[data-theme="dark"] .cc-forgot {
  color: #fcef60;
}

/* ── Submit button ─────────────────────────────────── */
.cc-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  background: #0092bf;
  border: none;
  border-radius: 3px;
  padding: 0.875rem 1rem;
  font-family: 'Barlow', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.10em;
  color: #ffffff;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.1s ease;
  margin-top: 0.25rem;
}

/* Shimmer sweep on hover */
.cc-submit::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.10) 50%, transparent 100%);
  transform: translateX(-100%);
  transition: transform 0.45s ease;
}

.cc-submit:hover::after {
  transform: translateX(100%);
}

.cc-submit:hover {
  background: #00a8d8;
  box-shadow: 0 6px 28px rgba(0, 146, 191, 0.42);
}

.cc-submit:active {
  transform: scale(0.99);
  background: #007da3;
}

.cc-submit__arrow {
  font-size: 1rem;
  display: inline-block;
  transition: transform 0.2s ease;
}

.cc-submit:hover .cc-submit__arrow {
  transform: translateX(4px);
}

/* ── Card footer ───────────────────────────────────── */
.cc-card__foot {
  margin-top: 2rem;
  padding: 1rem 2.5rem;
  border-top: 1px solid var(--cc-card-foot);
  display: flex;
  justify-content: center;
  transition: border-color 0.3s;
}

.cc-card__wordmark {
  font-family: 'Big Shoulders Display', sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.32em;
  color: var(--cc-t5);
  user-select: none;
  transition: color 0.3s;
}
</style>